import React, { useMemo, useState } from 'react';

import CallMadeIcon from '@mui/icons-material/CallMade';
import LaunchOutlinedIcon from '@mui/icons-material/LaunchOutlined';
import TabIcon from '@mui/icons-material/Tab';
import {
  Button,
  Chip,
  IconButton,
  Popover,
  Stack,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import * as changesets from 'json-diff-ts';
import { useSnackbar } from 'notistack';

import ConnectedDocsDrawer from './ConnectedDocsDrawer';
import { useUserData } from '../../../App/Component/UserDataProvider';
import { sortCounterPartyFirst } from '../../../Draft/Component/Helper';
import { useResponsive } from '../../../RiverusUI/Components/ResponsiveProvider';
import {
  fetchContractById,
  getAllConnectedDoc,
} from '../../../Services/DocumentLibrary';
import { renewAmendChipValue, submissionTypes } from '../DocLibStaticData';

interface Props {
  contractData: any;
}

const buttonStyle = {
  fontSize: '15px',
  textTransform: 'capitalize',
  lineHeight: '20px',
  alignItems: 'start',
  padding: 0,
  margin: 0,
  gap: '6px',
  width: 'fit-content',
};

const TableTooltip: React.FC<Props> = ({ contractData }) => {
  const { isMobile } = useResponsive();
  const [contractDetails, setContractDetails] = useState<any>({});
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);

  const { user_id, userIsAdmin } = useUserData();
  const { enqueueSnackbar } = useSnackbar();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setContractDetails({});
  };

  const handleCloseDrawer = () => {
    setOpenDrawer(false);
  };

  const { data: contract_data, isLoading: contractLoading } = useQuery({
    queryKey: ['get_contract_data_id', contractDetails?.id],
    queryFn: async () => {
      const response: any = await fetchContractById(contractDetails?.id);
      return response;
    },
    enabled: !!contractDetails?.id,
  });

  const { data: connectedDocs } = useQuery({
    queryKey: ['all-connected-docs', contract_data?.id],
    queryFn: () => getAllConnectedDoc(contract_data?.id),
    select: (response: any) => response?.results,
    enabled: !!contract_data?.id,
  });

  const masterContract = useMemo(
    () =>
      connectedDocs?.filter((item: any) => item?.uploaded_type === 'Contract'),
    [connectedDocs]
  );
  const amendmentContract = useMemo(
    () =>
      connectedDocs?.filter(
        (item: any) =>
          item?.uploaded_type !== 'Support Document' &&
          item?.uploaded_type !== 'Contract'
      ),
    [connectedDocs]
  );
  const supportContract = useMemo(
    () =>
      connectedDocs?.filter(
        (item: any) => item?.uploaded_type === 'Support Document'
      ),
    [connectedDocs]
  );

  const contracting_parties = useMemo(() => {
    const findPartyInParties = (id: any) => {
      return (contractData?.parties || []).find(
        (party: any) => party?.id === id
      );
    };
    const copyEdited =
      contractData?.contracting_parties?.[0]?.copy_edited_content
        ?.contracting_parties;

    // copy edited content present
    if (Array.isArray(copyEdited) && copyEdited?.length > 0) {
      return copyEdited?.map((cp: any) => {
        if (!cp?.id) return cp;
        const matched = findPartyInParties(cp.id);
        return matched
          ? {
              party: matched?.name ? matched?.name : cp?.party,
            }
          : cp;
      });
    }
    // Parties from master
    if (contractData?.parties?.length > 0) {
      return contractData?.parties;
    }
    // extraction type AI
    if (
      contractData?.extraction_type === 'ai' &&
      contractData?.contracting_parties?.[0]?.raw_content?.party?.data
    ) {
      return contractData?.contracting_parties?.[0]?.raw_content?.party?.data;
    }
    let changeList;
    if (contractData?.contracting_parties?.length > 0) {
      const diff = JSON.parse(
        JSON.stringify(contractData?.contracting_parties?.[0]?.edited_content)
      );
      const orig = JSON.parse(
        JSON.stringify(contractData?.contracting_parties?.[0]?.raw_content)
      );
      if (diff) {
        const unFlattenChanges = changesets.unflattenChanges(diff.update);
        changeList = JSON.parse(
          JSON.stringify(
            changesets.applyChangeset(
              JSON.parse(JSON.stringify(orig)),
              unFlattenChanges
            )
          )
        );
      } else {
        changeList = JSON.parse(
          JSON.stringify(
            changesets.applyChangeset(JSON.parse(JSON.stringify(orig)), diff)
          )
        );
      }
      return changeList.contracting_parties;
    }
    return changeList;
  }, [
    contractData?.parties,
    contractData?.contracting_parties,
    contractData?.extraction_type,
  ]);

  const sortedByParties = useMemo(
    () => sortCounterPartyFirst(contracting_parties),
    [contracting_parties]
  );

  const isOwner = useMemo(
    () => contractData?.creator_details?.id === user_id,
    [contractData?.creator_details, user_id]
  );

  const userIsCoOwner = useMemo(
    () => contractData?.owners?.find((owner: any) => owner?.id === user_id),
    [contractData?.owners, user_id]
  );

  const userCanDoAction = useMemo(
    () => isOwner || userIsAdmin || userIsCoOwner,
    [isOwner, userIsAdmin, userIsCoOwner]
  );

  const handleOpenContract = (contractId: string) => {
    window.open('/document/documentlibrary/' + btoa(contractId), '_blank');
  };

  const isExpiredContract = useMemo(() => {
    if (!contractData?.data?.end_date) return false;

    const endDate = new Date(contractData?.data?.end_date);
    const currentDate = new Date();

    // Set both dates to midnight (00:00:00) to compare only the date part
    endDate.setHours(0, 0, 0, 0);
    currentDate.setHours(0, 0, 0, 0);

    return endDate < currentDate;
  }, [contractData?.data]);

  const seniorReviewerIds = useMemo(
    () =>
      contract_data?.senior_reviewer?.map((assignee: any) => assignee?.id) ||
      [],
    [contract_data?.senior_reviewer]
  );

  const businessReviewerIds = useMemo(
    () =>
      contract_data?.business_reviewer?.map((assignee: any) => assignee?.id) ||
      [],
    [contract_data?.business_reviewer]
  );

  const businessSpocIds = useMemo(
    () =>
      contract_data?.business_spoc?.map((assignee: any) => assignee?.id) || [],
    [contract_data?.business_spoc]
  );

  const ownersWithRoles = useMemo(() => {
    const ownersList = contract_data?.owners ? [...contract_data.owners] : [];
    const creator = contract_data?.creator_details;

    // Add creator as Owner if business_spoc is empty
    if (
      (!contract_data?.business_spoc ||
        contract_data.business_spoc.length === 0) &&
      creator
    ) {
      const isCreatorInOwners = ownersList?.some(
        (owner: any) => owner?.id === creator?.id
      );
      if (!isCreatorInOwners) {
        ownersList.push(creator);
      }
    }

    //  Mapping assignees to roles
    const mappedOwners = ownersList?.map((owner: any) => {
      const roles: string[] = [];
      // Reviewer roles
      if (businessReviewerIds?.includes(owner?.id))
        roles.push('Business Reviewer');
      if (seniorReviewerIds?.includes(owner?.id))
        roles.push('Senior Legal Reviewer');

      // Owner logic
      if (businessSpocIds.includes(owner.id)) {
        roles.push('Owner');
      } else if (
        (!contract_data?.business_spoc ||
          contract_data.business_spoc.length === 0) &&
        creator?.id === owner.id
      ) {
        roles.push('Owner');
      }
      if (roles.length === 0) roles.push('Co-owner');
      return {
        id: owner.id,
        name: `${owner.first_name} ${owner.last_name}`,
        roles,
      };
    });
    const rolePriority = [
      'Owner',
      'Co-owner',
      'Business Reviewer',
      'Senior Legal Reviewer',
    ];
    return mappedOwners.sort((a, b) => {
      const aPriority = rolePriority.findIndex((role) =>
        a.roles.includes(role)
      );
      const bPriority = rolePriority.findIndex((role) =>
        b.roles.includes(role)
      );
      return aPriority - bPriority;
    });
  }, [
    contract_data?.owners,
    contract_data?.creator_details,
    seniorReviewerIds,
    businessReviewerIds,
    businessSpocIds,
  ]);

  const canAccessContract = (contractId: string) => {
    if (userCanDoAction) {
      handleOpenContract(contractId?.toString());
    } else {
      enqueueSnackbar('You are not assigned any role for this contract!', {
        variant: 'info',
      });
    }
  };

  return (
    <React.Fragment>
      <IconButton
        onClick={(event) => {
          handleClick(event);
          setContractDetails(contractData);
        }}
        aria-describedby="table_popover"
      >
        <CallMadeIcon style={{ color: '#6D264C' }} />
      </IconButton>

      <Popover
        id="table_popover"
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        sx={{ width: '80%', height: '80%' }}
      >
        <Stack direction="column" spacing={1} paddingTop="20px">
          <Stack direction="column" spacing={1} padding="0 20px">
            <Stack direction="row" flexWrap="wrap" gap={1}>
              <Typography fontSize="15px" fontWeight={600}>
                Title:
              </Typography>
              <Typography
                sx={{
                  fontSize: '15px',
                  maxWidth: '300px',
                  whiteSpace: 'wrap',
                  wordWrap: 'break-word',
                }}
              >
                {contractDetails?.title}
              </Typography>
              {contractData && !contractData?.terminate_status && (
                <>
                  {isExpiredContract ? (
                    <Chip
                      label="Expired"
                      sx={{ background: '#ff4d4f', color: 'white' }}
                    />
                  ) : (
                    <Chip
                      label="Active"
                      sx={{
                        background: '#87d068',
                        color: 'white',
                      }}
                    />
                  )}
                </>
              )}
              {contractData?.terminate_status &&
              contractData?.is_renewed !== 'Latest_Terminate' ? (
                contractData?.is_renewed === 'Under_Terminate' ? (
                  <Chip label="Under Termination" sx={{ ml: 1 }} />
                ) : (
                  <Chip label="Terminated" />
                )
              ) : (
                <>
                  {contractData?.renewal_draft_type === 'support_template' &&
                    contractData?.is_renewed === 'Latest_Terminate' && (
                      <Chip
                        label="Termination document"
                        sx={{ ml: 1, background: '#CDE7BD', color: '#184d14' }}
                      />
                    )}
                  {contractData?.is_renewed === 'Under_Renewal' && (
                    <Chip
                      label="Under Renewal"
                      sx={{ ml: 1, background: '#C4DBFF', color: '#1D192B' }}
                    />
                  )}
                  {contractData?.is_renewed === 'Under_Amend' && (
                    <Chip
                      label="Under Amendment"
                      sx={{ ml: 1, background: '#C4DBFF', color: '#1D192B' }}
                    />
                  )}
                  {contractData?.is_renewed === 'Renewed' && (
                    <Chip
                      label={
                        contractData?.renewal_draft_type === 'support_template'
                          ? 'Extension document'
                          : 'Renewed Version Available'
                      }
                      sx={{ ml: 1, background: '#CDE7BD', color: '#184d14' }}
                    />
                  )}
                  {contractData?.is_renewed === 'Amended' && (
                    <Chip
                      label="Amend Version Available"
                      sx={{ ml: 1, background: '#CDE7BD', color: '#184d14' }}
                    />
                  )}
                  {contractData?.is_renewed === 'Latest_Renewed' &&
                    !isExpiredContract && (
                      <Chip
                        label="Latest Renewed"
                        sx={{ ml: 1, background: '#CDE7BD', color: '#184d14' }}
                      />
                    )}
                  {contractData?.is_renewed === 'Latest_Amend' &&
                    !isExpiredContract && (
                      <Chip
                        label={
                          contractData?.renewal_draft_type ===
                          'support_template'
                            ? 'Amendment document'
                            : 'Latest Amended'
                        }
                        sx={{ ml: 1, background: '#CDE7BD', color: '#184d14' }}
                      />
                    )}
                </>
              )}

              {renewAmendChipValue(contractData?.docID) && (
                <Chip
                  label={renewAmendChipValue(contractData?.docID)}
                  sx={{ background: '#FFECF1' }}
                />
              )}
            </Stack>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              <Typography fontSize="15px" fontWeight={600}>
                Nature:
              </Typography>
              {contractDetails?.contract_type?.map(
                (item: any, index: number) => (
                  <Typography key={index} fontSize="15px">
                    {item?.name}
                  </Typography>
                )
              )}
            </Stack>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              <Typography fontSize="15px" fontWeight={600}>
                Expiry:
              </Typography>
              <Typography fontSize="15px">
                {contractDetails?.end_date
                  ? dayjs(contractDetails?.end_date).format('DD-MM-YYYY')
                  : 'N/A'}
              </Typography>
            </Stack>
            {sortedByParties?.length > 0 && (
              <Stack direction="column" spacing={1}>
                <Typography fontSize="15px" fontWeight={600}>
                  Contracting Parties
                </Typography>
                {sortedByParties?.map((item: any, index: number) => {
                  const displayName =
                    item?.party || item?.partyName || item?.name || item?.value;
                  return (
                    <Typography key={index} fontSize="15px">
                      {index + 1}). {displayName}
                    </Typography>
                  );
                })}
              </Stack>
            )}
            {connectedDocs?.length > 0 && (
              <Stack direction="column" spacing={1}>
                <Typography fontSize="15px" fontWeight={600}>
                  Connected Documents
                </Typography>
                {masterContract?.length > 0 &&
                  masterContract?.map((item: any) => (
                    <Stack
                      key={item?.id}
                      spacing={2}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography
                        sx={{
                          fontSize: '15px',
                          maxWidth: '300px',
                          whiteSpace: 'wrap',
                          wordWrap: 'break-word',
                        }}
                      >
                        1).{' '}
                        <span
                          style={{ cursor: 'pointer' }}
                          onClick={() => {
                            if (!item?.deleted_status)
                              canAccessContract(item?.id);
                          }}
                        >
                          {item?.title}
                        </span>
                      </Typography>
                      <Stack spacing={0.5} direction="row">
                        <Chip sx={{ background: '#FFECF1' }} label="Master" />
                        {item?.deleted_status && <Chip label="Delete" />}
                      </Stack>
                    </Stack>
                  ))}
                {amendmentContract?.length > 0 && (
                  <Stack direction="column" spacing={1}>
                    <Typography fontSize="14px" fontWeight={600}>
                      Amendment Documents
                    </Typography>

                    {amendmentContract?.map((item: any, index: number) => (
                      <Stack
                        direction="row"
                        key={index}
                        justifyContent="space-between"
                        alignItems="center"
                        spacing={1}
                      >
                        <Typography
                          sx={{
                            fontSize: '15px',
                            maxWidth: '300px',
                            whiteSpace: 'wrap',
                            wordWrap: 'break-word',
                          }}
                        >
                          {index + 1}).{' '}
                          <span
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                              if (!item?.deleted_status)
                                canAccessContract(item?.id);
                            }}
                          >
                            {item?.title}
                          </span>
                        </Typography>
                        <Stack direction="row" sx={{ gap: 1 }}>
                          {item?.submission_type && (
                            <Chip
                              sx={{ background: '#FFECF1' }}
                              label={submissionTypes?.[item?.submission_type]}
                            />
                          )}
                          {item?.deleted_status && <Chip label="Delete" />}
                        </Stack>
                      </Stack>
                    ))}
                  </Stack>
                )}
                {supportContract?.length > 0 && (
                  <Stack direction="column" spacing={1}>
                    <Typography fontSize="14px" fontWeight={600}>
                      Support Documents
                    </Typography>

                    {supportContract?.map((item: any, index: number) => (
                      <Stack
                        direction="row"
                        key={index}
                        justifyContent="space-between"
                        alignItems="center"
                        spacing={1}
                      >
                        <Typography
                          fontSize="15px"
                          sx={{
                            fontSize: '15px',
                            maxWidth: '500px',
                            whiteSpace: 'wrap',
                            wordWrap: 'break-word',
                          }}
                        >
                          {index + 1}). {item?.title}
                        </Typography>{' '}
                        {item?.deleted_status && <Chip label="Delete" />}
                      </Stack>
                    ))}
                  </Stack>
                )}
              </Stack>
            )}
            <Stack direction="column" spacing={1}>
              <Typography fontSize="15px" fontWeight={600}>
                Assignees
              </Typography>
              <Stack spacing={2}>
                {ownersWithRoles.map((item: any, index: number) => (
                  <Stack
                    key={item.id}
                    direction="row"
                    gap={1}
                    justifyContent={isMobile ? 'unset' : 'space-between'}
                    alignItems="start"
                    flexWrap={isMobile ? 'wrap' : 'unset'}
                  >
                    <Typography fontSize="15px">
                      {index + 1}). {item.name}
                    </Typography>
                    <Stack
                      direction="row"
                      flexWrap="wrap"
                      justifyContent={isMobile ? 'start' : 'end'}
                      width="min-content"
                      gap={1}
                    >
                      {item.roles
                        .slice()
                        .sort((a: any, b: any) => {
                          const rolePriority = [
                            'Owner',
                            'Co-owner',
                            'Business Reviewer',
                            'Senior Legal Reviewer',
                          ];
                          return (
                            rolePriority.indexOf(a) - rolePriority.indexOf(b)
                          );
                        })
                        .map((role: string, idx: number) => (
                          <Chip
                            key={idx}
                            label={role}
                            sx={{ background: '#FFECF1' }}
                          />
                        ))}
                    </Stack>
                  </Stack>
                ))}
                <Typography fontSize="15px">
                  Uploaded on{' '}
                  {dayjs(contractDetails?.created_on).format(
                    'MMMM D, YYYY h:mm A'
                  )}
                </Typography>
              </Stack>
            </Stack>
          </Stack>
          <Stack
            flexDirection={isMobile ? 'column' : 'row'}
            justifyContent="space-between"
            alignItems="center"
            width="100%"
            sx={{ padding: '0 16px 16px', gap: '14px', width: 'max-content' }}
          >
            <Button
              startIcon={<LaunchOutlinedIcon />}
              sx={buttonStyle}
              onClick={() => {
                handleOpenContract(contractDetails?.id?.toString());
              }}
              disabled={contractDetails?.deleted_status}
            >
              Open Contract
            </Button>
            <Button
              startIcon={<TabIcon />}
              sx={buttonStyle}
              onClick={() => setOpenDrawer(true)}
            >
              Show connected documents
            </Button>
          </Stack>
        </Stack>
      </Popover>
      {openDrawer && (
        <ConnectedDocsDrawer
          open={openDrawer}
          onClose={handleCloseDrawer}
          contractLoading={contractLoading}
          userCanDoAction={userCanDoAction}
          masterContract={masterContract}
          amendmentContract={amendmentContract}
          supportContract={supportContract}
        />
      )}
    </React.Fragment>
  );
};

export default TableTooltip;
